/**
 * useAssistantStore — shared, persisted state for the AI-marketer assistant
 * (FIX-AI-6).
 *
 * The assistant used to live on a single page with its conversation in a local
 * ref — switching pages or reloading lost it. As a floating widget it must
 * survive both, so the conversation (and the open/closed state) live here and
 * are mirrored to localStorage. AiAssistantPage reads `history`/`prompt` from
 * this store; the widget toggles `open`.
 */
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import type { AssistantTurn } from "../api/ai-assistant";

const CHAT_KEY = "cdp-assistant-chat-v1";
const OPEN_KEY = "cdp-assistant-open-v1";
const MAX_TURNS = 50; // cap persisted history so storage stays small

function loadHistory(): AssistantTurn[] {
  try {
    const raw = JSON.parse(localStorage.getItem(CHAT_KEY) || "[]");
    return Array.isArray(raw) ? (raw as AssistantTurn[]) : [];
  } catch {
    return [];
  }
}

function loadOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

export const useAssistantStore = defineStore("assistant", () => {
  const history = ref<AssistantTurn[]>(loadHistory());
  const prompt = ref("");
  /** Whether the slide-over panel is open (the ✨ launcher stays either way). */
  const open = ref(loadOpen());

  watch(history, (h) => {
    try { localStorage.setItem(CHAT_KEY, JSON.stringify(h.slice(-MAX_TURNS))); } catch { /* best-effort */ }
  }, { deep: true });

  watch(open, (o) => {
    try { localStorage.setItem(OPEN_KEY, o ? "1" : "0"); } catch { /* best-effort */ }
  });

  function clear(): void {
    history.value = [];
  }
  function toggle(): void {
    open.value = !open.value;
  }
  function show(): void {
    open.value = true;
  }
  function hide(): void {
    open.value = false;
  }

  return { history, prompt, open, clear, toggle, show, hide };
});
